import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { Client } from '../types';
import { supabase_merge_clients } from '../services/supabaseService';
import { X, Building2, AlertTriangle, ArrowRightLeft, ShieldCheck, Trash2, CheckCircle2, Loader2 } from 'lucide-react';

interface MergeClientModalProps {
    onClose: () => void;
    onMergeSuccess: () => void;
}

const MergeClientModal: React.FC<MergeClientModalProps> = ({ onClose, onMergeSuccess }) => {
    const [clients, setClients] = useState<Client[]>([]);
    const [isLoadingClients, setIsLoadingClients] = useState(true);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const { data, error } = await supabase.from('clients').select('id, name, address_city, client_type').order('name');
                if (error) throw error;
                setClients(data || []);
            } catch (err) {
                console.error("Error fetching clients for merge:", err);
            } finally {
                setIsLoadingClients(false);
            }
        };
        fetchClients();
    }, []);

    const [searchA, setSearchA] = useState('');
    const [searchB, setSearchB] = useState('');
    
    // In actual selection, we'll store the ID
    const [companyAId, setCompanyAId] = useState<string | null>(null);
    const [companyBId, setCompanyBId] = useState<string | null>(null);
    
    const [primaryCompanyId, setPrimaryCompanyId] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    const companyA = clients.find(c => c.id === companyAId);
    const companyB = clients.find(c => c.id === companyBId);

    const filteredClientsForA = clients.filter(c => 
        c.name.toLowerCase().includes(searchA.toLowerCase()) && c.id !== companyBId
    ).slice(0, 10);

    const filteredClientsForB = clients.filter(c => 
        c.name.toLowerCase().includes(searchB.toLowerCase()) && c.id !== companyAId
    ).slice(0, 10);

    const handleMerge = async () => {
        if (!companyA || !companyB || !primaryCompanyId) {
            setError("Zorg ervoor dat je twee bedrijven selecteert én kiest welke het hoofdbedrijf is.");
            return;
        }

        const sourceId = primaryCompanyId === companyA.id ? companyB.id : companyA.id;
        const targetId = primaryCompanyId;

        setIsProcessing(true);
        setError(null);
        try {
            await supabase_merge_clients(sourceId, targetId);
            onMergeSuccess();
            onClose();
        } catch (err: any) {
            console.error("Samenvoegen mislukt:", err);
            setError(err.message || "Er is een fout opgetreden bij het samenvoegen van de bedrijven.");
            setIsProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={onClose} />
            
            <div className="bg-white w-full max-w-3xl rounded-3xl shadow-2xl relative z-10 flex flex-col overflow-hidden animate-scaleIn">
                {/* Header */}
                <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center text-[#e51b23]">
                            <ArrowRightLeft size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-slate-900">Bedrijven Samenvoegen</h2>
                            <p className="text-xs text-slate-500 font-medium">Kies 2 dubbele bedrijven en transfer de data.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-900 transition-colors bg-white rounded-full shadow-sm hover:shadow-md">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {error && (
                        <div className="bg-red-50 text-[#e51b23] p-4 rounded-xl text-sm font-bold flex items-start gap-3 border border-red-100 animate-slideDown">
                            <AlertTriangle size={20} className="shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Company A Selection */}
                        <div className="space-y-4">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center justify-between">
                                Bedrijf 1 {companyAId && <button onClick={() => { setCompanyAId(null); if(primaryCompanyId === companyAId) setPrimaryCompanyId(companyBId) }} className="text-red-500 hover:text-red-700">Wissen</button>}
                            </label>
                            
                            {!companyAId ? (
                                <div className="space-y-2 relative">
                                    <input 
                                        type="text" 
                                        placeholder="Zoek een bedrijf..." 
                                        value={searchA}
                                        onChange={(e) => setSearchA(e.target.value)}
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-indigo-500 transition-all"
                                    />
                                    {searchA && (
                                        <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg z-20">
                                            {filteredClientsForA.map(c => (
                                                <button 
                                                    key={c.id} 
                                                    onClick={() => { setCompanyAId(c.id); setSearchA(''); if(!primaryCompanyId) setPrimaryCompanyId(c.id); }}
                                                    className="w-full text-left px-4 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 text-sm font-bold text-slate-800 flex items-center justify-between"
                                                >
                                                    {c.name}
                                                    <span className="text-[10px] text-slate-400 uppercase">{c.address_city}</span>
                                                </button>
                                            ))}
                                            {filteredClientsForA.length === 0 && <div className="p-4 text-xs text-slate-500 text-center">Niks gevonden</div>}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div 
                                    className={`p-5 rounded-2xl border-2 transition-all cursor-pointer ${primaryCompanyId === companyA.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                                    onClick={() => setPrimaryCompanyId(companyA.id)}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${primaryCompanyId === companyA.id ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                            <Building2 size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-black text-slate-900">{companyA?.name}</h3>
                                            <p className="text-xs text-slate-500">{companyA?.address_city || 'Onbekend'} • {companyA?.client_type}</p>
                                        </div>
                                    </div>
                                    <div className="mt-4 flex items-center justify-center">
                                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${primaryCompanyId === companyA.id ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>
                                            {primaryCompanyId === companyA.id ? <><ShieldCheck size={14}/> Hoofdbedrijf</> : <><Trash2 size={14}/> Wordt gewist</>}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Company B Selection */}
                        <div className="space-y-4">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center justify-between">
                                Bedrijf 2 {companyBId && <button onClick={() => { setCompanyBId(null); if(primaryCompanyId === companyBId) setPrimaryCompanyId(companyAId) }} className="text-red-500 hover:text-red-700">Wissen</button>}
                            </label>
                            
                            {!companyBId ? (
                                <div className="space-y-2 relative">
                                    <input 
                                        type="text" 
                                        placeholder="Zoek bedrijf om samen te voegen..." 
                                        value={searchB}
                                        onChange={(e) => setSearchB(e.target.value)}
                                        className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-indigo-500 transition-all"
                                    />
                                    {searchB && (
                                        <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg z-20">
                                            {filteredClientsForB.map(c => (
                                                <button 
                                                    key={c.id} 
                                                    onClick={() => { setCompanyBId(c.id); setSearchB(''); if(!primaryCompanyId) setPrimaryCompanyId(companyAId || c.id); }}
                                                    className="w-full text-left px-4 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 text-sm font-bold text-slate-800 flex items-center justify-between"
                                                >
                                                    {c.name}
                                                    <span className="text-[10px] text-slate-400 uppercase">{c.address_city}</span>
                                                </button>
                                            ))}
                                            {filteredClientsForB.length === 0 && <div className="p-4 text-xs text-slate-500 text-center">Niks gevonden</div>}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div 
                                    className={`p-5 rounded-2xl border-2 transition-all cursor-pointer ${primaryCompanyId === companyB.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                                    onClick={() => setPrimaryCompanyId(companyB.id)}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${primaryCompanyId === companyB.id ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                            <Building2 size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-black text-slate-900">{companyB?.name}</h3>
                                            <p className="text-xs text-slate-500">{companyB?.address_city || 'Onbekend'} • {companyB?.client_type}</p>
                                        </div>
                                    </div>
                                    <div className="mt-4 flex items-center justify-center">
                                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${primaryCompanyId === companyB.id ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>
                                            {primaryCompanyId === companyB.id ? <><ShieldCheck size={14}/> Hoofdbedrijf</> : <><Trash2 size={14}/> Wordt gewist</>}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {companyAId && companyBId && (
                        <div className="bg-indigo-900 p-6 rounded-2xl flex items-start gap-4 text-indigo-100 mt-6 animate-fadeIn">
                            <AlertTriangle size={24} className="text-orange-400 shrink-0 mt-1" />
                            <div className="text-sm font-medium leading-relaxed">
                                <p className="mb-2"><strong>Let op:</strong> Alle data van <b>{primaryCompanyId === companyA.id ? companyB?.name : companyA.name}</b> zoals openstaande <i>vacatures, contactcontactpersonen, facturatie instellingen, kandidaten-verbindingen (shortlist) en documenten</i> worden overgeheveld naar het geselecteerde <b>Hoofdbedrijf</b>.</p>
                                <p className="text-orange-300 font-bold">Nadat de overdracht is voltooid wordt het verliezende profiel permanent gewist.</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 mt-auto shrink-0 z-20 relative shadow-[0_-10px_20px_rgba(0,0,0,0.02)]">
                    <button 
                        onClick={onClose} 
                        className="px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest text-slate-500 hover:bg-slate-200 transition-colors"
                    >
                        Annuleren
                    </button>
                    <button 
                        onClick={handleMerge}
                        disabled={!companyAId || !companyBId || !primaryCompanyId || isProcessing}
                        className="flex items-center gap-2 px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest text-white bg-[#e51b23] hover:bg-red-700 disabled:opacity-50 disabled:hover:bg-[#e51b23] transition-all shadow-md active:scale-95"
                    >
                        {isProcessing ? "Verwerken..." : <><CheckCircle2 size={16}/> Definitief Samenvoegen</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MergeClientModal;
